'use client';

import * as React from 'react';
import { cx } from '../utils';
import { JIcon } from '../../icons';
import { devWarn } from '../internal/devWarn';
import type { StyledProps } from '../types';
import type { UnsafeStyleProps } from '../escape-hatches';

export interface TransferOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface TransferProps extends StyledProps, UnsafeStyleProps {
  options: TransferOption[];
  /** Target-pool membership in insertion order. Defaults to []. */
  value?: string[];
  onChange?: (value: string[]) => void;
  /**
   * Default `false`: user moves emit a sanitised value (IDs not in
   * `options` are dropped). This matches the common case where the
   * adopter owns the option universe and any value not in `options`
   * is stale/forbidden.
   *
   * Set `true` for async / paged / permission-filtered option sources
   * where unknown IDs are *transiently* missing but still valid in the
   * parent's data model. In preserve mode, moves keep unknown IDs
   * intact; only the parent can explicitly drop them.
   *
   * (Reconciles Codex iter-3 vs iter-5: iter-3 flagged silent
   * revocations during transient gaps; iter-5 flagged invisible-
   * persistence in true-removal flows. The two contracts are
   * mutually exclusive — the adopter picks via this flag.)
   */
  preserveUnknownValues?: boolean;
  /** Visible header for the source pool. */
  sourceHeader?: React.ReactNode;
  /** Visible header for the target pool. */
  targetHeader?: React.ReactNode;
  /** Render search input above each pool. */
  withSearch?: boolean;
  size?: 'sm' | 'md';
  disabled?: boolean;
  error?: boolean;
  /** Adopter aria-label for the root group. */
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  testId?: string;
}

type Side = 'source' | 'target';

export function Transfer({
  options,
  value = [],
  onChange,
  preserveUnknownValues = false,
  sourceHeader = 'Available',
  targetHeader = 'Selected',
  withSearch = false,
  size = 'md',
  disabled = false,
  className,
  unsafe_style,
  error,
  testId,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}: TransferProps) {
  const reactId = React.useId();
  const sourceLabelId = `${reactId}-source-label`;
  const targetLabelId = `${reactId}-target-label`;

  const [marked, setMarked] = React.useState<{ source: Set<string>; target: Set<string> }>({
    source: new Set(),
    target: new Set(),
  });
  const [filter, setFilter] = React.useState({ source: '', target: '' });
  const [focusedValue, setFocusedValue] = React.useState<Record<Side, string | null>>({
    source: null,
    target: null,
  });
  const [announce, setAnnounce] = React.useState('');
  const optRefs = React.useRef<Map<string, HTMLButtonElement>>(new Map());

  /* Dev-warn duplicates + drop marked keys that no longer exist OR have
     become disabled. Without this, a parent that prunes / disables an
     option can leave ghost keys in `marked` that survive a "Move marked"
     click and end up in `value`. */
  React.useEffect(() => {
    const seen = new Set<string>();
    const enabledValues = new Set<string>();
    for (const o of options) {
      if (seen.has(o.value)) {
        devWarn(`[JKit.Transfer] duplicate option value "${o.value}"; selection will misbehave.`);
      }
      seen.add(o.value);
      if (!o.disabled) enabledValues.add(o.value);
    }
    setMarked((m) => {
      const sourceClean = new Set([...m.source].filter((v) => enabledValues.has(v)));
      const targetClean = new Set([...m.target].filter((v) => enabledValues.has(v)));
      if (sourceClean.size === m.source.size && targetClean.size === m.target.size) return m;
      return { source: sourceClean, target: targetClean };
    });
  }, [options]);

  /* Map keyed by canonical value → option metadata. Lets the move
     functions enforce the disabled contract without re-scanning the
     options array per call. */
  const optionByValue = React.useMemo(() => new Map(options.map((o) => [o.value, o])), [options]);

  /* Sanitize the controlled value to drop IDs the parent removed
     from options.

     Codex iter-1 flagged that moves leaked stale IDs back through
     `[...value]`; Codex iter-2 flagged the inverse — automatically
     emitting a "corrected" value would destructively prune
     selections during a transient async-options loading gap.

     Resolution: keep sanitization for VIEW + MOVE OUTPUT (so stale
     IDs disappear from pools and never re-enter `value` via a user
     move), but never call `onChange` from a render/effect. Stale
     IDs stay in `value` until a user-initiated move emits a
     sanitized result. Dev-warns once per session so adopters notice
     the gap. */
  const sanitizedValue = React.useMemo(
    () => value.filter((v) => optionByValue.has(v)),
    [value, optionByValue],
  );

  const warnedUnknownRef = React.useRef<string>('');
  React.useEffect(() => {
    if (sanitizedValue.length === value.length) return;
    const unknownKey = value
      .filter((v) => !optionByValue.has(v))
      .sort()
      .join(',');
    if (warnedUnknownRef.current === unknownKey) return;
    warnedUnknownRef.current = unknownKey;
    devWarn(
      `[JKit.Transfer] ${value.length - sanitizedValue.length} value(s) not in ` +
        'options are hidden from view; they will be dropped on the next user move. ' +
        'If this is intentional async-loading state, this warning is informational ' +
        'only — the kit will NOT mutate `value` without an explicit user action.',
    );
  }, [sanitizedValue, value, optionByValue]);

  const sourceItems = options.filter((o) => !sanitizedValue.includes(o.value));
  const targetItems = sanitizedValue
    .map((v) => optionByValue.get(v))
    .filter((o): o is TransferOption => !!o);

  const filtered = (items: TransferOption[], side: Side) => {
    const q = filter[side].trim().toLowerCase();
    if (!q) return items;
    return items.filter((o) => o.label.toLowerCase().includes(q));
  };

  const sourceVisible = filtered(sourceItems, 'source');
  const targetVisible = filtered(targetItems, 'target');

  /* Filter against both `optionByValue` (existence) and `o.disabled`
     (the disabled contract). Codex review flagged that Ctrl/Cmd+A
     could mark disabled options and then leak them through move-marked. */
  const enabledOnly = (vals: string[]): string[] =>
    vals.filter((v) => {
      const o = optionByValue.get(v);
      return !!o && !o.disabled;
    });

  const moveToTarget = (vals: string[]) => {
    if (disabled) return;
    const safe = enabledOnly(vals);
    if (safe.length === 0) return;
    /* Base on `preserveUnknownValues ? value : sanitizedValue`.
       Default `false` drops unknowns (matches the common
       adopter-owns-options case, per Codex iter-5). Opt-in `true`
       preserves them for async/paged sources, per Codex iter-3. */
    const base = preserveUnknownValues ? value : sanitizedValue;
    const next = [...base];
    for (const v of safe) if (!next.includes(v)) next.push(v);
    onChange?.(next);
    setMarked((m) => ({ ...m, source: new Set() }));
    setAnnounce(`Moved ${safe.length} ${safe.length === 1 ? 'item' : 'items'} to selected.`);
  };

  const moveToSource = (vals: string[]) => {
    if (disabled) return;
    const safe = enabledOnly(vals);
    if (safe.length === 0) return;
    /* Same base selection as moveToTarget. */
    const base = preserveUnknownValues ? value : sanitizedValue;
    onChange?.(base.filter((v) => !safe.includes(v)));
    setMarked((m) => ({ ...m, target: new Set() }));
    setAnnounce(`Moved ${safe.length} ${safe.length === 1 ? 'item' : 'items'} to available.`);
  };

  const moveAllToTarget = () =>
    moveToTarget(sourceVisible.filter((o) => !o.disabled).map((o) => o.value));
  const moveAllToSource = () =>
    moveToSource(targetVisible.filter((o) => !o.disabled).map((o) => o.value));
  const moveMarkedToTarget = () => moveToTarget(Array.from(marked.source));
  const moveMarkedToSource = () => moveToSource(Array.from(marked.target));

  const toggleMark = (side: Side, val: string) => {
    if (disabled) return;
    const opt = optionByValue.get(val);
    if (!opt || opt.disabled) return;
    setMarked((m) => {
      const set = new Set(m[side]);
      if (set.has(val)) set.delete(val);
      else set.add(val);
      return { ...m, [side]: set };
    });
  };

  const visibleFor = (side: Side) => (side === 'source' ? sourceVisible : targetVisible);

  const onPoolKey = (e: React.KeyboardEvent, side: Side) => {
    if (disabled) return;
    const list = visibleFor(side);
    if (list.length === 0) return;
    const cur = focusedValue[side] ?? list[0]!.value;
    const idx = Math.max(
      0,
      list.findIndex((o) => o.value === cur),
    );
    let handled = true;
    let nextIdx = idx;
    if (e.key === 'ArrowDown') nextIdx = Math.min(list.length - 1, idx + 1);
    else if (e.key === 'ArrowUp') nextIdx = Math.max(0, idx - 1);
    else if (e.key === 'Home') nextIdx = 0;
    else if (e.key === 'End') nextIdx = list.length - 1;
    else if (e.key === ' ') toggleMark(side, list[idx]!.value);
    else if (e.key === 'Enter') {
      if (side === 'source') moveToTarget([list[idx]!.value]);
      else moveToSource([list[idx]!.value]);
    } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a') {
      /* Mark all ENABLED options in the focused pool. Disabled options
         stay unmarked so move-marked never violates the disabled
         contract (Codex review #high). */
      setMarked((m) => ({
        ...m,
        [side]: new Set(list.filter((o) => !o.disabled).map((o) => o.value)),
      }));
    } else if (e.key === 'Escape') {
      setMarked((m) => ({ ...m, [side]: new Set() }));
    } else handled = false;
    if (handled) {
      e.preventDefault();
      if (nextIdx !== idx) {
        const nv = list[nextIdx]!.value;
        setFocusedValue((f) => ({ ...f, [side]: nv }));
        if (e.shiftKey) toggleMark(side, nv);
        queueMicrotask(() => optRefs.current.get(`${side}:${nv}`)?.focus());
      }
    }
  };

  const renderPool = (
    side: Side,
    items: TransferOption[],
    label: React.ReactNode,
    labelId: string,
  ) => {
    const markedSet = marked[side];
    /* Roving tabindex target: must be (a) still visible after any
       search filter AND (b) NOT disabled. A disabled `<button>` is
       removed from the tab order by every browser; if it claimed
       `tabIndex=0` the listbox would be keyboard-unreachable
       (regression flagged in regular review when the first visible
       option happens to be disabled).
       Codex iter-9's fix to TimePicker carried the same shape; this
       mirrors it on the Transfer side. */
    const isUsable = (o: TransferOption | undefined) => !!o && !o.disabled;
    const focusedItem = focusedValue[side]
      ? items.find((o) => o.value === focusedValue[side])
      : undefined;
    const tabbable = isUsable(focusedItem)
      ? focusedItem!.value
      : (items.find(isUsable) ?? items[0])?.value;
    const total = side === 'source' ? sourceItems.length : targetItems.length;
    const filterActive = filter[side].trim().length > 0;
    const countText = filterActive
      ? `${items.length} of ${total} ${total === 1 ? 'item' : 'items'}`
      : `${items.length} ${items.length === 1 ? 'item' : 'items'}`;
    return (
      <div className="j-transfer-pool" data-side={side} data-error={error || undefined}>
        <div id={labelId} className="j-transfer-pool-header">
          {label}
          <span className="j-transfer-pool-count">{countText}</span>
        </div>
        {withSearch && (
          <input
            type="search"
            className="j-transfer-search"
            placeholder="Search…"
            value={filter[side]}
            onChange={(e) => setFilter((f) => ({ ...f, [side]: e.target.value }))}
            aria-label={`Search ${side}`}
            disabled={disabled}
          />
        )}
        <div
          className="j-transfer-listbox"
          role="listbox"
          aria-multiselectable="true"
          aria-labelledby={labelId}
          aria-disabled={disabled || undefined}
          tabIndex={-1}
          onKeyDown={(e) => onPoolKey(e, side)}
        >
          {items.length === 0 ? (
            <div className="j-transfer-empty" role="presentation">
              {side === 'source' ? 'No more items' : 'Nothing selected'}
            </div>
          ) : (
            items.map((o) => {
              const isMarked = markedSet.has(o.value);
              const isTab = o.value === tabbable;
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  ref={(el) => {
                    const k = `${side}:${o.value}`;
                    if (el) optRefs.current.set(k, el);
                    else optRefs.current.delete(k);
                  }}
                  className="j-transfer-option"
                  aria-selected={isMarked}
                  aria-disabled={o.disabled || disabled || undefined}
                  data-marked={isMarked || undefined}
                  tabIndex={isTab ? 0 : -1}
                  disabled={o.disabled || disabled}
                  onClick={() => !o.disabled && toggleMark(side, o.value)}
                  onDoubleClick={() => {
                    if (o.disabled || disabled) return;
                    if (side === 'source') moveToTarget([o.value]);
                    else moveToSource([o.value]);
                  }}
                  onFocus={() => setFocusedValue((f) => ({ ...f, [side]: o.value }))}
                >
                  {o.label}
                </button>
              );
            })
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className={cx('j-transfer', `j-transfer-${size}`, className)}
      style={unsafe_style}
      data-jq-id={testId}
      role="group"
      aria-label={ariaLabelledBy ? undefined : ariaLabel || 'Transfer between lists'}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      aria-disabled={disabled || undefined}
    >
      {/* j-transfer-layout is a DESCENDANT of j-transfer's container,
          so its @container rule actually applies. Container queries
          don't restyle the container itself — the previous structure
          left the 3-column layout intact at narrow widths. */}
      <div className="j-transfer-layout">
        {renderPool('source', sourceVisible, sourceHeader, sourceLabelId)}
        <div className="j-transfer-actions" role="group" aria-label="Transfer actions">
          <button
            type="button"
            className="j-transfer-action"
            onClick={moveMarkedToTarget}
            disabled={disabled || marked.source.size === 0}
            aria-label={`Move ${marked.source.size} marked to selected`}
          >
            <JIcon intent="chevron-right" size={16} aria-hidden />
          </button>
          <button
            type="button"
            className="j-transfer-action"
            onClick={moveAllToTarget}
            /* Gate on enabled-visibility, not raw visibility — if the
               source pool only has disabled options left (e.g. an
               un-removable "Admin"), `moveToTarget` filters them all
               out and returns silently. Reading "enabled" but doing
               nothing is misleading; disable the button instead. */
            disabled={disabled || !sourceVisible.some((o) => !o.disabled)}
            aria-label="Move all visible to selected"
          >
            <JIcon intent="arrow-right" size={16} aria-hidden />
          </button>
          <button
            type="button"
            className="j-transfer-action"
            onClick={moveMarkedToSource}
            disabled={disabled || marked.target.size === 0}
            aria-label={`Move ${marked.target.size} marked to available`}
          >
            <JIcon intent="chevron-left" size={16} aria-hidden />
          </button>
          <button
            type="button"
            className="j-transfer-action"
            onClick={moveAllToSource}
            /* Same enabled-visibility gate as moveAllToTarget. */
            disabled={disabled || !targetVisible.some((o) => !o.disabled)}
            aria-label="Move all visible to available"
          >
            <JIcon intent="arrow-left" size={16} aria-hidden />
          </button>
        </div>
        {renderPool('target', targetVisible, targetHeader, targetLabelId)}
      </div>
      <div className="j-transfer-announce" role="status" aria-live="polite">
        {announce}
      </div>
    </div>
  );
}
